<template>
  <div>
    <HeaderTabs
      :show-breadcrumbs="true"
      :is-root-account="isRootAccount"
      :tabs-list="accountTabs"
    />
    <SpinnerContainer
      v-if="appLoading"
      :is-loading="appLoading"
      class="nested-tab-page-spinner"
    />
    <router-view />
  </div>
</template>

<script>
  import HeaderTabs from 'components/common/HeaderTabs/HeaderTabs';
  import translation from 'translation';
  import {mapGetters} from 'vuex';
  import {SpinnerContainer} from '@marilyn/marilyn-ui';

  export default {
    name: 'AccountsNestedPage',
    components: {HeaderTabs, SpinnerContainer},
    computed: {
      ...mapGetters('common', ['appLoading']),
      ...mapGetters('accounts', ['currentAccount', 'hasComponent']),
      subItemsLink () {
        if (this.currentAccount.type === 'MCC') {
          return {
            id: 'list',
            title: translation.tr('Subaccounts'),
            to: {name: 'account'},
            exactMatch: true,
            forceActiveByRoutes: ['accountCreate']
          };
        }
        return {
          id: 'projects',
          title: translation.tr('Projects'),
          to: {name: 'projectsList'},
          forceActiveByRoutes: ['projectCreate']
        };
      },
      accountIsUsual () {
        return this.currentAccount.type === 'USUAL';
      },
      isRootAccount () {
        return !this.currentAccount?.parent_id;
      },
      accountTabs () {
        if (!this.currentAccount) {
          return [];
        }

        const connectionWizardTitle = this.pgettext('CREATE_CONNECTION', 'Ad platform connection wizard');
        const waConnectionWizardTitle = this.pgettext('WA_CONNECTION', 'Web analytics connection wizard');

        return [
          this.subItemsLink,
          {
            id: 'roles',
            title: translation.tr('Users roles'),
            to: {name: 'accountRoles'}
          },
          {
            id: 'connections',
            title: translation.tr('Connections'),
            matchBy: 'connections_nested',
            to: {name: 'connectionsList'},
            forceRouteTitles: {
              connectionCreate: connectionWizardTitle,
              connectionCreateSubconnection: connectionWizardTitle,
              connectionCreatePendingSubconnection: connectionWizardTitle,
              connectionCurrencySetup: connectionWizardTitle,

              // web analytics
              webanalyticsConnectionCreate: waConnectionWizardTitle,
              webanalyticsConnectionSetup: waConnectionWizardTitle,
              webanalyticsPendingConnectionSetup: waConnectionWizardTitle
            }
          },
          {
            id: 'orders',
            title: translation.tr('Orders'),
            to: {name: 'accountOrders'}
          },
          {
            id: 'statistics',
            title: translation.tr('Statistics'),
            to: {name: 'accountStatistics'}
          },
          {
            id: 'accountHistory',
            title: translation.tr('History'),
            to: {name: 'accountHistory'}
          },
          {
            id: 'settings',
            title: translation.tr('Settings'),
            to: {
              name: 'accountSettings',
              params: {
                accountId: this.currentAccount.id
              }
            }
          },
          {
            id: 'mediaplans',
            title: translation.tr('Mediaplans'),
            to: {name: 'roreMediaplansList'}
          },
          {
            id: 'reference',
            title: translation.tr('Reference'),
            items: [
              {
                id: 'payers',
                title: translation.tr('Payers'),
                to: {name: 'payersList'}
              },
              {
                id: 'advertisers',
                title: translation.tr('Advertisers'),
                to: {name: 'advertisersList'}
              },
              {
                id: 'additionalServices',
                title: translation.tr('Additional Services'),
                to: {name: 'additionalServicesList'}
              },
              {
                id: 'brands',
                title: translation.tr('Brands'),
                to: {name: 'brandsList'}
              },
              {
                id: 'contracts',
                title: translation.tr('Contracts'),
                to: {name: 'contractsList'}
              },
              {
                id: 'customers',
                title: translation.tr('Customers'),
                to: {name: 'customersList'}
              },
              {
                id: 'companies',
                title: translation.tr('Companies'),
                to: {name: 'companiesList'}
              },
              {
                id: 'markups',
                title: translation.tr('Markups'),
                to: {name: 'markupsList'}
              }
            ]
          },
          {
            id: 'syncSources',
            title: translation.tr('Sync sources'),
            to: {name: 'syncSourcesList'}
          },
          {
            id: 'roreReference',
            title: translation.tr('RoRe Reference'),
            items: [
              {
                id: 'roreSuppliers',
                title: translation.trc('rore', 'Suppliers'),
                to: {name: 'roreSuppliersList'}
              },
              {
                id: 'roreSites',
                title: translation.trc('rore', 'Sites'),
                to: {name: 'roreSitesList'}
              },
              {
                id: 'roreSitePlatforms',
                title: translation.trc('rore', 'Site Platforms'),
                to: {name: 'roreSitePlatformsList'}
              },
              {
                id: 'roreSiteSalesHouses',
                title: translation.trc('rore', 'Site Sales Houses'),
                to: {name: 'roreSiteSalesHousesList'}
              },
              {
                id: 'roreSiteTypes',
                title: translation.trc('rore', 'Site Types'),
                to: {name: 'roreSiteTypesList'}
              },
              {
                id: 'rorePlaceContexts',
                title: translation.trc('rore', 'Place Contexts'),
                to: {name: 'rorePlaceContextsList'}
              },
              {
                id: 'rorePages',
                title: translation.trc('rore', 'Pages'),
                to: {name: 'rorePagesList'}
              },
              {
                id: 'rorePlaces',
                title: translation.trc('rore', 'Places'),
                to: {name: 'rorePlacesList'}
              },
              {
                id: 'roreFormats',
                title: translation.trc('rore', 'Formats'),
                to: {name: 'roreFormatsList'}
              },
              {
                id: 'roreFormatTypes',
                title: translation.trc('rore', 'Format Types'),
                to: {name: 'roreFormatTypesList'}
              },
              {
                id: 'roreRotationTypes',
                title: translation.trc('rore', 'Rotation Types'),
                to: {name: 'roreRotationTypesList'}
              },
              {
                id: 'roreCoefficients',
                title: translation.trc('rore', 'Coefficients'),
                to: {name: 'roreCoefficientsList'}
              },
              {
                id: 'rorePriceTypes',
                title: translation.trc('rore', 'Price Types'),
                to: {name: 'rorePriceTypesList'}
              },
              {
                id: 'roreStrategies',
                title: translation.trc('rore', 'Strategies'),
                to: {name: 'roreStrategiesList'}
              },
              {
                id: 'roreDisclaimers',
                title: translation.trc('rore', 'Disclaimers'),
                to: {name: 'roreDisclaimersList'}
              },
              {
                id: 'roreTechCosts',
                title: translation.trc('rore', 'Tech costs'),
                to: {name: 'roreTechCostsList'}
              }
            ]
          },
          this.isRootAccount ? {
            id: 'finances',
            title: translation.tr('Finances'),
            items: [
              {
                id: 'planFact',
                title: translation.tr('Plan-Fact'),
                to: {name: 'planFact'}
              }
            ]
          } : null,
          {
            id: 'financialReports',
            title: translation.tr('Financial Reports'),
            items: [
              {
                id: 'billReport',
                title: translation.tr('Bill report'),
                to: {name: 'billReport'}
              }
            ]
          },
          {
            id: 'closure',
            title: translation.tr('Closure'),
            items: [
              ...this.isRootAccount ? [
                {
                  id: 'brokerReports',
                  title: translation.tr('Import Yandex acts'),
                  to: {name: 'brokerReports'}
                },
                {
                  id: 'brokerPlacementsUnlinked',
                  title: translation.tr('Broker unlinked placements'),
                  to: {name: 'brokerPlacementsUnlinkedList'}
                },
                {
                  id: 'brokerPlacementsExcluded',
                  title: translation.tr('Broker excluded placements'),
                  to: {name: 'brokerPlacementsExcludedList'}
                },
                {
                  id: 'accountingReport',
                  title: translation.tr('Accounting report'),
                  to: {name: 'accountingReport'}
                }
              ] : [],
              {
                id: 'bookingRegistryReport',
                title: translation.tr('Booking registry report'),
                to: {name: 'bookingRegistryReport'}
              },
              {
                id: 'closurePlacementsSpending',
                title: translation.tr('Placements spending'),
                to: {name: 'closurePlacementsSpending'}
              },
              {
                id: 'closureReport',
                title: translation.tr('Closure report'),
                to: {name: 'closureReport'}
              },
              {
                id: 'reviseReports',
                title: translation.tr('Revise reports'),
                to: {name: 'reviseReports'}
              }
            ]
          },
          {
            id: 'tools',
            title: translation.tr('Tools'),
            items: [
              {
                id: 'gs-exporter',
                title: translation.tr('Export statistics into Google Sheets'),
                to: {name: 'gs-exporter-list'}
              }
            ]
          }
        ].filter(x => Boolean(x));
      }
    }
  };
</script>
